import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CatalogKind, CatalogTerm } from '@fsm/shared';
import { api } from '../lib/api';
import { keys } from '../lib/queries';
import { Button, Input, Select, cx } from './ui';

/**
 * One of the office's own lists, chosen or invented on the spot.
 *
 * They discover their own entries while they are on the phone — somebody says
 * "I saw your van" and there is no "Van" on the list. Making them stop, go to
 * Settings, add it, come back and re-open the record is how a field ends up
 * empty on half the records, and a half-filled field answers no questions at
 * the end of the quarter. So "Add new…" is in the dropdown itself.
 */
export function CatalogPicker({
  kind,
  value,
  onChange,
  placeholder,
  disabled,
  className,
}: {
  kind: CatalogKind;
  value: string | null | undefined;
  onChange: (termId: string | null) => void;
  /** What the "add one" box suggests — the words differ per list. */
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data: terms = [] } = useQuery({
    queryKey: keys.catalog(kind),
    queryFn: () => api.catalog.list(kind),
    select: (d) => d.items,
    staleTime: 5 * 60_000,
  });

  const create = useMutation({
    mutationFn: () => api.catalog.create({ kind, name: name.trim() }),
    onSuccess: (created: CatalogTerm) => {
      void qc.invalidateQueries({ queryKey: keys.catalog(kind) });
      setAdding(false);
      setName('');
      setError(null);
      onChange(created.id);
    },
    onError: (e) => setError((e as { message?: string }).message ?? 'Could not add that'),
  });

  /*
   * A term removed after this record was tagged with it still has to appear in
   * this one dropdown, or opening an old job would silently re-tag it as
   * "Not set" the next time anybody saved.
   */
  const selectedIsMissing = Boolean(value) && !terms.some((t) => t.id === value);

  if (adding) {
    return (
      <div className={cx('space-y-2', className)}>
        <div className="flex gap-2">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={placeholder ?? 'Add to the list…'}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter' && name.trim()) create.mutate();
              if (e.key === 'Escape') setAdding(false);
            }}
          />
          <Button
            variant="primary"
            disabled={!name.trim()}
            loading={create.isPending}
            onClick={() => create.mutate()}
          >
            Add
          </Button>
          <Button onClick={() => { setAdding(false); setError(null); }}>Cancel</Button>
        </div>
        {error && <p className="text-[12px] text-danger">{error}</p>}
      </div>
    );
  }

  return (
    <Select
      className={className}
      disabled={disabled}
      value={value ?? ''}
      onChange={(e) => {
        if (e.target.value === '__new__') {
          setAdding(true);
          return;
        }
        onChange(e.target.value || null);
      }}
    >
      <option value="">Not set</option>
      {selectedIsMissing && <option value={value!}>(removed)</option>}
      {terms.map((term) => (
        <option key={term.id} value={term.id}>
          {term.name}
        </option>
      ))}
      <option value="__new__">+ Add a new one…</option>
    </Select>
  );
}
